import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH } from '../../constants.ts';
import { GET_MICROTECH_BUERO_PLUS_INTEGRATIONS, UPDATE_MICROTECH_BUERO_PLUS_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    baseUrl: '',
    clientId: '',
    clientSecret: '',
    authState: '',
    orderTransactionType: '',
    rfqTransactionType: '',
    offerErpSyncTransactionType: '',
    orderDocumentSysArt: '',
    rfqDocumentSysArt: '',
    orderChannelIds: [],
    rfqChannelIds: [],
};

const MicrotechBueroPlusIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('microtechBueroPlus.title')}`;

    const { data, refetch } = useQuery(GET_MICROTECH_BUERO_PLUS_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.microtechBueroPlusIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                baseUrl: integration.baseUrl,
                // write-only: the stored credentials are never sent to the browser, so these
                // start empty and only replace what is stored when actually filled in
                clientId: '',
                clientSecret: '',
                authState: integration.authState,
                orderTransactionType: integration.orderTransactionType ?? '',
                rfqTransactionType: integration.rfqTransactionType ?? '',
                offerErpSyncTransactionType: integration.offerErpSyncTransactionType ?? '',
                orderDocumentSysArt: integration.orderDocumentSysArt ?? '',
                rfqDocumentSysArt: integration.rfqDocumentSysArt ?? '',
                orderChannelIds: integration.orderChannels?.map((c) => c.id) ?? [],
                rfqChannelIds: integration.rfqChannels?.map((c) => c.id) ?? [],
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_MICROTECH_BUERO_PLUS_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    baseUrl: formData.baseUrl,
                    // omitted entirely when left blank, so a save that is not a rotation keeps
                    // the working credentials and does not re-authenticate
                    ...(formData.clientId ? { clientId: formData.clientId } : {}),
                    ...(formData.clientSecret ? { clientSecret: formData.clientSecret } : {}),
                    orderTransactionType: formData.orderTransactionType,
                    rfqTransactionType: formData.rfqTransactionType,
                    offerErpSyncTransactionType: formData.offerErpSyncTransactionType,
                    orderDocumentSysArt: formData.orderDocumentSysArt,
                    rfqDocumentSysArt: formData.rfqDocumentSysArt,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('microtechBueroPlus.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH));
        } catch (error) {
            console.error(error);
            const message =
                error instanceof Error && error.message ? error.message : t('microtechBueroPlus.updateError');
            publishToast({ description: message, status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            {
                                label: t('microtechBueroPlus.title'),
                                to: url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH),
                            },
                            { label: integration?.name || t('microtechBueroPlus.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('microtechBueroPlus.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('microtechBueroPlus.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="baseUrl"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.baseUrl')}
                                    helpText={t('microtechBueroPlus.form.baseUrlHelp')}
                                    error={formErrors.baseUrl}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="clientId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.clientId')}
                                    helpText={t('microtechBueroPlus.form.credentialsHelp')}
                                    error={formErrors.clientId}
                                >
                                    <StringField {...field} autoComplete="off" />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="clientSecret"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.clientSecret')}
                                    error={formErrors.clientSecret}
                                >
                                    <StringField {...field} type="password" autoComplete="new-password" />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authState"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('microtechBueroPlus.table.authState')} error={formErrors.authState}>
                                    <StringField {...field} readOnly={true} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderTransactionType"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.orderTransactionType')}
                                    helpText={t('microtechBueroPlus.form.orderTransactionTypeHelp')}
                                    error={formErrors.orderTransactionType}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="rfqTransactionType"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.rfqTransactionType')}
                                    helpText={t('microtechBueroPlus.form.rfqTransactionTypeHelp')}
                                    error={formErrors.rfqTransactionType}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="offerErpSyncTransactionType"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.offerErpSyncTransactionType')}
                                    helpText={t('microtechBueroPlus.form.offerErpSyncTransactionTypeHelp')}
                                    error={formErrors.offerErpSyncTransactionType}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderDocumentSysArt"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.orderDocumentSysArt')}
                                    helpText={t('microtechBueroPlus.form.orderDocumentSysArtHelp')}
                                    error={formErrors.orderDocumentSysArt}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.orderChannelIds')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="rfqDocumentSysArt"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.rfqDocumentSysArt')}
                                    helpText={t('microtechBueroPlus.form.rfqDocumentSysArtHelp')}
                                    error={formErrors.rfqDocumentSysArt}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="rfqChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('microtechBueroPlus.form.rfqChannelIds')}
                                    error={formErrors.rfqChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH)}>
                                    {t('microtechBueroPlus.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default MicrotechBueroPlusIntegrationEditPage;
